import React from 'react'

import { combineClasses } from '~/util'

import { FavoritesAction } from '../../buttons'
import { Label } from '../../labels'
import { ItemName } from '../../titles'
import { UnitPrice } from '../../unit-price'
import { UnitQuickFacts } from '../../unit-quick-facts'
import { DoorCodeTimer } from '../../door-code'

import { Card, CardProps } from '../Card'

import styles from './UnitCard.module.scss'
import { FloorPlanImage } from '~/components'
import { Nilable } from 'tsdef'
import { ANALYTICS_CATEGORIES, LockDevice, Unit } from '~/service'

interface DetailsProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onLockError'> {
  disabled: boolean
  title: string
  price: Nilable<number>
  beds: Nilable<number>
  baths: Nilable<number>
  footage: Nilable<number>
  lock: Nilable<LockDevice>
  onLockError: (name: string, error: unknown) => void
  isFavorite?: boolean
  onToggleFavorite: Nilable<(e: React.MouseEvent) => void>
  isAvailableForLease: boolean
  isAvailableForTour: boolean
  showUnitPrices: boolean
  showUntourableUnits: boolean
  isModel: boolean
  showModelPrices: boolean
}

export function Details({
  disabled,
  title,
  price,
  beds,
  baths,
  footage,
  lock,
  onLockError,
  isFavorite,
  onToggleFavorite,
  isAvailableForLease,
  isAvailableForTour,
  showUnitPrices,
  showModelPrices,
  showUntourableUnits,
  isModel,
  ...rest
}: DetailsProps) {
  const hasDetails = beds || baths || footage || lock?.access

  return (
    <div
      className={combineClasses(
        styles.details,
        lock?.access ? styles.hasDoorCode : styles.noDoorCode
      )}
      {...rest}
    >
      <div className={styles.headerRow}>
        {title && (
          <ItemName className={styles.itemTitle} data-testid="unitTitle">
            {title}
          </ItemName>
        )}
        <UnitPrice
          className={styles.itemPrice}
          data-test="price"
          price={price}
          isAvailableForLease={isAvailableForLease}
          isAvailableForTour={isAvailableForTour}
          showUnitPrices={showUnitPrices}
          showUntourableUnits={showUntourableUnits}
          isModel={isModel}
          showModelPrices={showModelPrices}
        />
      </div>

      {hasDetails && (
        <div className={styles.details}>
          <UnitQuickFacts
            className={styles.quickFacts}
            bedroomCount={beds}
            bathroomCount={baths}
            squareFootageCount={footage}
          />

          <div className={styles.doorCodeAndActions}>
            <div
            // This div ensures the favorites button is right justified even
            // if the door code is not shown.
            >
              {lock?.access && !disabled && isAvailableForTour && (
                <DoorCodeTimer
                  className={styles.DoorCode}
                  label="Unlock"
                  accessType={lock.accessType}
                  access={lock.access}
                  startTime={lock.startTime}
                  endTime={lock.endTime}
                  onLockError={onLockError}
                />
              )}
            </div>
            {onToggleFavorite && !disabled && (
              <FavoritesAction
                data-testid="FavoritesAction"
                className={styles.FavoritesAction}
                isToggled={isFavorite}
                onClick={onToggleFavorite}
                category={ANALYTICS_CATEGORIES.UNIT}
                action={isFavorite ? 'Remove Favorite' : 'Add Favorite'}
                label={title}
                button
              >
                {isFavorite ? 'Remove' : 'Add'} Favorite
              </FavoritesAction>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export interface UnitCardProps extends CardProps {
  /**
   * Unit object.
   */
  unit: Unit
  /**
   * Usually the unit number and is displayed in the blue unitNumber Card.
   */
  label: Nilable<string | number>
  /**
   * string to be passed to set the title of Apartment Destination
   * if not passed unit.number and floor.plate name will be used.
   */
  title?: string
  /**
   * determines if unit prices should be displayed.
   */
  showUnitPrices: boolean
  /**
   * Whether display price of a model unit.
   */
  showModelPrices: boolean
  showUntourableUnits: boolean
  /**
   * dermines if the Unit floorPlate should be displayed.
   */
  showFloorPlanImage?: Nilable<boolean>
  /**
   * If this is specified, then the favorites button
   * will be shown, allowing the user to add/remove
   * the unit from their favorites.
   */
  onToggleFavorite: Nilable<(...args: unknown[]) => unknown>
  /**
   * Whether the unit is currently in the users favorites.
   */
  isFavorite?: boolean
  /**
   * called when floorPlanImage is loaded.
   */
  onLoad?: (...args: unknown[]) => unknown
  lock: Nilable<LockDevice>
  onLockError: (name: string, error: unknown) => void
  onFloorPlanMaximized?: (e: React.MouseEvent) => void
  onFloorPlanMinimized?: (e: React.MouseEvent) => void
  floorPlanDisclaimerText?: Nilable<string>
}

export function UnitCard({
  unit,
  label,
  title = unit.floorPlan?.name ?? '',
  showUnitPrices,
  showModelPrices,
  showUntourableUnits,
  onLoad,
  showFloorPlanImage: _showFloorPlanImage,
  className,
  isFavorite,
  onToggleFavorite,
  lock,
  onLockError,
  onFloorPlanMaximized,
  onFloorPlanMinimized,
  floorPlanDisclaimerText,
  disabled: _disabled,
  ...rest
}: UnitCardProps) {
  const cardRef = React.useRef(null)

  const showFloorPlanImage = !!_showFloorPlanImage && !!unit.floorPlan?.imageUrl

  const disabled =
    _disabled ||
    // If this is not a model and it's unavailable for lease
    (!unit.isModel && !unit.isAvailableForLease) ||
    // If we want to hide units that can't be toured and this unit can't be toured
    (!showUntourableUnits && !unit.isAvailableForTour)

  return (
    <Card
      ref={cardRef}
      data-testid="UnitCard"
      className={combineClasses(
        styles.UnitCard,
        !showFloorPlanImage ? styles.row : styles.column,
        !unit.floorPlan?.imageUrl ? styles.noImage : '',
        className
      )}
      disabled={disabled}
      {...rest}
    >
      <div className={styles.content}>
        {label && (
          <div
            className={combineClasses(
              styles.unitNumberContainer,
              showFloorPlanImage && styles.withFloorPlanImage
            )}
          >
            <Label
              data-testid="label"
              children={label}
              size="m"
              display="primary"
              disabled={disabled}
            />
          </div>
        )}
        {unit.isModel && <span className={styles.modelTag}>Model</span>}
        {showFloorPlanImage && (
          <FloorPlanImage
            containerClassName={styles.floorPlanImageContainer}
            className={styles.floorPlanImage}
            src={unit.floorPlan!.imageUrl}
            alt={`${unit.floorPlan?.name} Floor Plan`}
            onLoad={onLoad}
            disabled={disabled}
            onMaximize={onFloorPlanMaximized}
            onMinimize={onFloorPlanMinimized}
            disclaimerText={floorPlanDisclaimerText}
          />
        )}
        <Details
          data-testid="unit"
          disabled={disabled}
          title={title}
          price={unit.price}
          isAvailableForLease={!!unit.isAvailableForLease}
          isAvailableForTour={!!unit.isAvailableForTour}
          showUnitPrices={!!showUnitPrices}
          showUntourableUnits={!!showUntourableUnits}
          isModel={!!unit.isModel}
          showModelPrices={!!showModelPrices}
          beds={unit.floorPlan?.bedrooms}
          baths={unit.floorPlan?.bathrooms}
          footage={unit.floorPlan?.squareFootage}
          lock={lock}
          onLockError={onLockError}
          isFavorite={isFavorite}
          onToggleFavorite={onToggleFavorite}
        />
      </div>
    </Card>
  )
}
